/** The town screen.
 *
 * One painted backdrop plus one sprite per structure, placed on the shared slot
 * grid in `townSlots.ts`. The rule that makes it work: every building in an
 * upgrade chain shares one slot, a slot draws exactly ONE building - the
 * highest built level in its chain - and an unbuilt structure is simply not
 * drawn. Hovering an empty slot fades in a ghost of what would stand there, so
 * the screen is discoverable without printing a list over the painting.
 *
 * Nothing here decides anything (I10). Legality arrives as a `Reason` from the
 * host, which got it from the kernel, and every refusal the player reads is
 * that `Reason` rendered.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type {
  ArmyId, ArmyState, BuildingDef, BuildingDefId, ContentRegistry, CreatureDefId, GameCommand,
  HeroDefId, HeroState, Reason, ResourceKind, Resources, TownState, UpgradeOffer,
} from "@game/core";
import { townBackgroundKey } from "./art.js";
import { groupDigits, titleCase } from "./format.js";
import { crestKey } from "./formArt.js";
import { ScreenStyles, useMeasure } from "./primitives.js";
import { explainReason, townIncome } from "./townModel.js";
import type { ResolvedSlot } from "./townSlots.js";
import {
  airRows, drawOrder, hasOwnLayout, resolveTownSlots, skyBand, skyLift, stageLayout,
} from "./townSlots.js";
import type { Prop } from "./townScenery.js";
import { groundKind, townScenery } from "./townScenery.js";
import { townLife } from "./townAnim.js";
import { TownLife, useLifeClock } from "./townLife.js";
import { TownBackdrop } from "./townScene.js";
import { townTracks } from "./townTracks.js";
import { TownTracks } from "./townTracksView.js";
import { townFootings, townProps } from "./townProps.js";
import { TownPropHD } from "./townPropsView.js";
import { BitmapText } from "../theme/bitmapText.js";
import { usePhoneLayout } from "../theme/phone.js";
import { armyDropCommand } from "./heroModel.js";
import { townClickSwallowed, townSocketTap } from "./townArmyPointer.js";
import { Marketplace } from "./Marketplace.js";
import type { TradeRate } from "./Marketplace.js";
import {
  HeroFace, TownArmyBlock, TownConsoleHead, TownFlag, TownIconButton, TownResourceStrip,
  TownStyles, bustSkyVars, dwellingLines, useTownStats, velvetVars,
} from "./townChrome.js";
import type { DragState, DropModifiers, TownStats } from "./townChrome.js";
import {
  AirScrim, FarGrade, NearLight, TownBuilding, TownPaintStyles, TownProp, TownStreets, WarmScrim,
  airBackAlpha, airFarAlpha, airSplitIndex, airlight, depth, warmth,
} from "./town/scene.js";
import type { StageItem } from "./town/scene.js";
import {
  BuildDialog, BuildListDialog, CaravanDialog, DismissHeroDialog, DismissStackDialog, GuildDialog,
  RecruitDialog, ShopDialog, SplitDialog, StackDialog, TavernDialog,
} from "./town/dialogs.js";
import type { SlotRef, TownCaravanInFlight, TownCaravanOffer } from "./town/dialogs.js";
export type { TownCaravanInFlight, TownCaravanOffer } from "./town/dialogs.js";

export interface TownScreenProps {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly treasury: Resources;
  readonly garrison: ArmyState | null;
  readonly visitingHero: HeroState | null;
  readonly visitingArmy: ArmyState | null;
  readonly day: number;
  /** Heroes the player's tavern offers this week. */
  readonly tavernOffers?: readonly HeroDefId[];
  /** From the kernel: null means "legal right now". */
  readonly buildReason: (b: BuildingDefId) => Reason | null;
  readonly recruitReason?: (c: CreatureDefId, count: number) => Reason | null;
  /**
   * The kernel's `availableOf`: how many of a creature this town can recruit
   * right now - its own pool plus the base form's, since the two forms of a
   * tier draw from one dwelling. The screen used to read `town.available`
   * itself with a one-level chain of its own, which is a second opinion on
   * which pool backs which creature; this is the kernel's, by way of the host.
   */
  readonly available: (creature: CreatureDefId) => number;
  /**
   * The kernel's `townUpgradeOffers` for one of this town's armies: the
   * stacks it would upgrade and the bill for each. It is the same call the
   * kernel's UPGRADE_UNITS reads, so a stack the dialog offers is one the
   * town upgrades at the town's price, and one it does not offer gets no
   * button rather than a price the screen worked out for itself.
   */
  readonly upgradeOffers: (army: ArmyId) => readonly UpgradeOffer[];
  /**
   * Everywhere that could send creatures to this town, from the kernel.
   *
   * A rule output the screen may not derive (I10): what a source holds, what
   * the purse can carry and how many days the road is are all the kernel's.
   * Absent - on a fixture screen, or for a host that has not wired it - and the
   * Caravans button simply does not appear.
   */
  readonly caravanOffers?: readonly TownCaravanOffer[];
  /** Columns this player already has walking, soonest first. */
  readonly caravansOnTheRoad?: readonly TownCaravanInFlight[];
  readonly hireReason?: (h: HeroDefId) => Reason | null;
  /** The kernel's `tradeYield`, handed over by the host. Absent on a fixture
   *  screen, and the Marketplace slot then falls back to its build dialog. */
  readonly tradeRate?: TradeRate | undefined;
  readonly tradeReason?: ((give: ResourceKind, take: ResourceKind, amount: number) => Reason | null) | undefined;
  /**
   * Would the kernel refuse this? Null means "legal right now".
   *
   * The garrison rows need it per drop target rather than per command type: a
   * stack dragged onto a socket holding a different creature, the last stack
   * out of a hero's army and a merge onto a full socket are three different
   * refusals, and the row has to grey the right socket and print the right
   * sentence BEFORE the player lets go. The screen still decides nothing - it
   * asks and renders the answer.
   */
  readonly checkCommand?: (cmd: GameCommand) => Reason | null;
  readonly onCommand: (cmd: GameCommand) => void;
  readonly onClose: () => void;
  readonly onOpenHero?: (hero: HeroState) => void;
  /**
   * The owner's colour, which upholsters the console: the classic wraps the town
   * bar in the owning player's velvet, and it is the strongest brand cue the
   * screen has. Absent or unknown, the console keeps the kit's leather.
   */
  readonly ownerColour?: string | null;
  /**
   * Whether the life layer runs: flags, smoke, clouds, birds, glints. Off,
   * the clock holds at zero and the screen is the same still every time,
   * which is what a screenshot harness wants. Absent means on; the viewer's
   * reduced-motion preference also holds the clock (see useLifeClock).
   */
  readonly animations?: boolean;
}

type Modal =
  | { readonly kind: "none" }
  | { readonly kind: "build"; readonly building: BuildingDefId }
  | { readonly kind: "recruit"; readonly creature: CreatureDefId }
  | { readonly kind: "buildList" }
  | { readonly kind: "guild" }
  /** Reinforce this town: what everywhere else could send it, and how long. */
  | { readonly kind: "caravan" }
  | { readonly kind: "tavern" }
  | { readonly kind: "market" }
  /** The Blacksmith counter: war machines, bought straight into their slot. */
  | { readonly kind: "shop" }
  /** A shift-drag, or the Split button on the stack window. */
  | { readonly kind: "split"; readonly from: SlotRef; readonly to: SlotRef }
  /** One stack, clicked: what it is, and the two things you can do to it. */
  | { readonly kind: "stack"; readonly at: SlotRef }
  | { readonly kind: "dismissStack"; readonly at: SlotRef }
  | { readonly kind: "dismissHero" };

interface Hovered {
  readonly name: string;
  readonly note: string;
}

/** Slots whose built sprite opens something other than the build dialog. */
const FUNCTION_SLOT = new Set(["mage_guild", "tavern", "marketplace", "blacksmith"]);
/** See `scenery` in TownScreen: the back band's trees at the back band's scale. */
const BACK_PROP_SHRINK = 0.5;

/**
 * Whether the pixel-art scenery (townScenery: map-object trees, hedges,
 * benches, the two foreground occluders) is planted on the stage.
 *
 * Off since the hi-def backdrops of 2026-09-04. The painting carries its own
 * framing - the oaks at Castle's edges, Greenmantle's pines, Saltreach's palms and
 * tide pools - painted in its own perspective and light, and a 64px map pine
 * magnified 3x and pixelated on top of it is the one thing in the frame that
 * is not a painting. The plan is still computed by `townScenery` (its tests
 * hold), and the street plan's shadow layer still seats every building.
 */
const PAINTED_SCENERY = false;

export function TownScreen(props: TownScreenProps): JSX.Element {
  const { content, town } = props;
  const [stageRef, box] = useMeasure<HTMLDivElement>();
  const [modal, setModal] = useState<Modal>({ kind: "none" });
  const [hover, setHover] = useState<Hovered | null>(null);
  // What a drag is about to do, or why it cannot. It takes the same plate the
  // buildings use, which sits directly over the two army rows.
  const [notice, setNotice] = useState<Hovered | null>(null);
  const phone = usePhoneLayout();
  const [consoleOpen, setConsoleOpen] = useState(true);

  // The two armies a player can move troops between while he is standing here,
  // by the ids the kernel knows them by. The garrison is always one of them;
  // the other exists only while a hero is visiting, which is what makes
  // entering the town (HERO_TO_TOWN) the precondition for the whole bar.
  const armies = useMemo(() => {
    const m = new Map<string, ArmyState>();
    if (props.garrison !== null) m.set(props.garrison.id as string, props.garrison);
    if (props.visitingArmy !== null) m.set(props.visitingArmy.id as string, props.visitingArmy);
    return m;
  }, [props.garrison, props.visitingArmy]);

  const closeModal = useCallback(() => { setModal({ kind: "none" }); }, []);
  const issue = useCallback((cmd: GameCommand) => {
    props.onCommand(cmd);
    setModal({ kind: "none" });
    setNotice(null);
  }, [props]);

  const layout = stageLayout(box.w, box.h);
  const slots = useMemo(
    () => resolveTownSlots(content, town.faction as string, town.built),
    [content, town.faction, town.built],
  );
  const painted = useMemo(() => drawOrder(slots), [slots]);
  // Only a slot with something standing on it gets an approach, so the paving
  // grows with the town instead of describing one that is not there yet.
  const occupied = useMemo(
    () => painted.filter((r) => r.built !== null).map((r) => r.slot),
    [painted],
  );
  const layoutSlots = useMemo(() => painted.map((r) => r.slot), [painted]);
  // The ground between the buildings: a street along every stand that has
  // something built on it, and the painting's own roads between the stands
  // (townTracks.ts). Under the shadow layer, so the contact shadows land on
  // the street a building fronts rather than beside it.
  const tracks = useMemo(
    () => townTracks(town.faction as string, new Set(occupied.map((s) => s.name))),
    [town.faction, occupied],
  );
  const ground = groundKind(town.faction as string);
  // The treeline behind the back band is planted for a flat lawn: at 1.15 to
  // 1.57 of a 128px map pine it stood taller than the keep on the top shelf.
  // On the terraces the back band is a quarter of a mile off and drawn at
  // 2/3, so its trees are drawn at the band's own scale, which puts a pine at
  // a dwelling's height rather than at the keep's.
  const scenery = useMemo(
    () => (PAINTED_SCENERY ? townScenery(layoutSlots, town.faction as string)
      .map((prop) => (prop.band === "back" && !prop.fg
        ? { ...prop, scale: prop.scale * BACK_PROP_SHRINK }
        : prop)) : []),
    [layoutSlots, town.faction],
  );
  // The clutter on that ground: stalls, carts, fences, braziers, planted in
  // the gaps between the buildings that are actually built (townProps.ts).
  // What a blind critic named first was that every building stood alone in
  // its own moat of painted lawn; the classic's screens are packed.
  const clutter = useMemo(
    () => townProps(town.faction as string, new Set(occupied.map((s) => s.name))),
    [town.faction, occupied],
  );
  // What the PAINTING does not give a building to stand on: the bank, the
  // pilings, the cut shelf or the plinth under the slots whose stand
  // declares one (townProps.townFootings). Drawn as props, but each one
  // immediately before its own building in the painter's order.
  const footings = useMemo(
    () => townFootings(town.faction as string, new Set(occupied.map((s) => s.name))),
    [town.faction, occupied],
  );
  // Buildings and props share one painter's order, or a tree planted behind a
  // roof draws over it and the depth the whole layout is built on is gone.
  const stage = useMemo<readonly StageItem[]>(() => {
    const items: StageItem[] = painted.map((r) => ({ kind: "slot" as const, y: r.slot.y, x: r.slot.x, r }));
    scenery.forEach((prop, i) => { items.push({ kind: "prop", y: prop.y, x: prop.x, prop, i }); });
    clutter.forEach((hd, i) => { items.push({ kind: "hdprop", y: hd.y, x: hd.x, hd, i }); });
    footings.forEach((hd, i) => {
      items.push({ kind: "hdprop", y: hd.sortY ?? hd.y, x: hd.x, hd, i: 10000 + i });
    });
    return items.sort((a, b) => a.y - b.y || a.x - b.x);
  }, [painted, scenery, clutter, footings]);
  // The shadow layer seats props as well as buildings, and it measures a prop
  // by `58 * scale` (TownStreets): a prop's own drawn width over that 58 is
  // the scale that puts its contact shadow under IT rather than under a
  // notional 58-pixel object.
  const propShadows = useMemo<readonly Prop[]>(
    () => [
      ...scenery,
      ...clutter.map((c): Prop => ({
        art: c.art, x: c.x, y: c.y, scale: c.w / 58,
        band: c.band, flip: c.flip, fg: false, lit: false,
      })),
    ],
    [scenery, clutter],
  );
  const income = useMemo(() => townIncome(content, town), [content, town]);
  const stats = useTownStats(content, town);
  // What BUILD would offer, counted the same way `BuildListDialog` counts its
  // rows - a slot with a next step. `built`/`buildable` cannot answer it: a
  // chain contributes three entries to `buildable` and at most one row to the
  // list, so on a finished town the two disagreed and the button stayed lit.
  const remaining = useMemo(
    () => slots.filter((r) => r.next !== null).length,
    [slots],
  );
  const backgroundKey = townBackgroundKey(town.faction as string);
  const sky = skyLift(layout);
  const air = airlight(town.faction as string);
  const dep = depth(town.faction as string);
  const warm = warmth(town.faction as string);
  // Where the painter's order is cut for the second wash. `stage` is sorted by
  // ground line, so everything before this index is at or above AIR_SPLIT_Y and
  // everything after is below it: the slice cannot reorder two sprites.
  const rows = useMemo(() => airRows(town.faction as string), [town.faction]);
  const airSplit = useMemo(() => airSplitIndex(stage, rows.split), [stage, rows]);
  // A hi-def painting already carries its own aerial perspective - its
  // painter hazed the distance - so the first wash, the one over the
  // backdrop alone, is not laid on it; the second, which is what seats the
  // far band's buildings in that painted distance, still is. And the low
  // sun's warmth starts at the painting's own horizon.
  const ownPainting = hasOwnLayout(town.faction as string);
  const warmFrom = ownPainting ? skyBand(town.faction as string)[1] : warm.from;
  // What moves. A function of what is BUILT, so the plan changes when the
  // town does and not per frame; the clock alone changes per frame.
  const life = useMemo(
    () => townLife(town.faction as string, painted.filter((r) => r.built !== null)),
    [town.faction, painted],
  );
  const clock = useLifeClock(props.animations ?? true);

  const tradeable = props.tradeRate !== undefined && props.tradeReason !== undefined;
  const openSlot = useCallback((r: ResolvedSlot) => {
    // A built Marketplace opens the trade counter. On a fixture screen there is
    // no rate probe and so nothing honest to quote, and the slot behaves like
    // any other structure rather than showing a made-up rate.
    const functional = r.slot.name === "marketplace" ? tradeable : FUNCTION_SLOT.has(r.slot.name);
    if (r.built !== null && functional) {
      setModal({
        kind: r.slot.name === "tavern" ? "tavern"
          : r.slot.name === "marketplace" ? "market"
            : r.slot.name === "blacksmith" ? "shop" : "guild",
      });
      return;
    }
    if (r.built !== null && r.built.dwellingFor !== null) {
      setModal({ kind: "recruit", creature: r.built.dwellingFor });
      return;
    }
    if (r.next !== null) setModal({ kind: "build", building: r.next.id });
  }, [tradeable]);

  const describe = (def: BuildingDef, built: boolean): Hovered => ({
    name: built ? def.name : `Build ${def.name}`,
    note: def.description,
  });

  // One painter for both halves of the stage, so the two slices either side of
  // the second air wash draw exactly the same way.
  const paintStageItem = (item: StageItem): JSX.Element | null => (
    item.kind === "prop"
      ? <TownProp key={`p:${String(item.i)}`} prop={item.prop} />
      : item.kind === "hdprop"
        ? <TownPropHD key={`h:${String(item.i)}`} prop={item.hd} />
        : (
        <TownBuilding
          key={item.r.slot.name}
          resolved={item.r}
          ground={ground}
          onEnter={(d, b) => { setHover(describe(d, b)); }}
          onLeave={() => { setHover(null); }}
          onOpen={() => { openSlot(item.r); }}
          blocked={item.r.built === null && item.r.next !== null
            && props.buildReason(item.r.next.id) !== null}
        />
      )
  );

  return (
    <div className="hs-screen" role="region" aria-label={`${town.name}, ${titleCase(town.faction as string)}`}>
      <ScreenStyles />
      <TownStyles />
      <TownPaintStyles />

      <div className="hs-town" ref={stageRef}>
        <div className="hs-town__view">
          <div
            className="hs-town__canvas"
            data-ground={ground}
            /* The grade is keyed by FACTION, not by country: five factions
               stand on grass and Castle is the one of them that is already at
               parity with its reference, so a per-ground grade would have had
               to move the one picture that must not move. */
            data-faction={town.faction as string}
            style={{
              left: `${String(layout.offsetX)}px`,
              top: `${String(layout.offsetY)}px`,
              transform: `scale(${layout.scale.toFixed(4)})`,
            }}
            onMouseLeave={() => setHover(null)}
          >
            {/* The backdrop pays for the crop the stage takes, so the sky is
                in the frame. See `skyLift`: the painting is lowered onto the
                first row the player can see and squeezed into what is left,
                and the buildings do not move with it because they stand on
                the slot grid rather than on the picture. */}
            <TownBackdrop
              atlasKey={backgroundKey}
              title={`${titleCase(town.faction as string)} town`}
              style={{
                transformOrigin: "0 0",
                transform: `translateY(${sky.shift.toFixed(2)}px) scaleY(${sky.squeeze.toFixed(4)})`,
              }}
            />

            {/* The air in front of the painted hills. Between the backdrop and
                everything else, so it reaches the one plane that is entirely
                at the horizon and nothing that stands in front of it. */}
            {ownPainting ? null : <AirScrim rgb={air.rgb} alpha={airFarAlpha(air.alpha)} rows={rows} />}

            {/* The sky's life - clouds, birds, the glints on far water and the
                glow of lava and crystal - between the painting and everything
                that stands on it. No seam any more: the terraced backdrop
                paints its own ground down to the bottom edge, so there is no
                join between hills and lawn for a haze band to hide. */}
            <TownLife plan={life} layer="sky" t={clock} />

            <TownTracks plan={tracks} scale={layout.scale} />

            <TownStreets
              layout={layoutSlots}
              occupied={occupied}
              props={propShadows}
              scale={layout.scale}
              air={air.rgb}
              paved={false}
            />

            {stage.slice(0, airSplit).map(paintStageItem)}

            {/* The second wash, cut into the painter's order at the empty rows
                between the back band's floor and the middle band's roof: it
                lands on the back band's buildings and trees and on the far half
                of the ground, and never on anything a player stands in front
                of. The cut is by ground line rather than by band so it can only
                ever move the wash, never reorder two sprites that overlap. */}
            <AirScrim rgb={air.rgb} alpha={airBackAlpha(air.alpha)} rows={rows} />

            {/* The value half of the same cut. See FarGrade: the chroma the
                wash takes out of the distance measured right and the LUMA did
                not, so the second number rides the first one's stop table. */}
            <FarGrade amount={ownPainting ? dep.far * 0.5 : dep.far} rows={rows} />

            {/* And the hue half, on the same cut. Last of the three because
                it is the only one that must not change a value: it reads the
                luminance under it, so it has to read the graded one. */}
            <WarmScrim warm={{ amount: warm.amount, from: warmFrom }} lift={sky} rows={rows} />

            {stage.slice(airSplit).map(paintStageItem)}

            {/* Flags on the keep and the hall, smoke off the smithy, fog along
                the scarps: over the buildings they belong to. */}
            <TownLife plan={life} layer="over" t={clock} />

            {/* The sun on the near plane, over every sprite standing in it. */}
            <NearLight amount={dep.near} rows={rows} />

            {/* Light the frame throws, rather than light a sprite contains.
                Last inside the canvas and screened over everything in it, so
                Greenmantle gets the one value anchor its histogram cannot be
                graded into and Emberpit's ground fires reach the buildings
                standing over them. Empty on the towns that need neither. */}
            <div className="hs-town__glow" aria-hidden="true" />
          </div>
          <div className="hs-town__light" />
        </div>

        {/* Only a NOTICE - a refused drag, a purchase that failed - takes the
            parchment card. A hovered building is named on the console's
            status line, which is the reference's own and only place for it;
            a card floating in the painting on every hover hid the buildings
            and was the first thing a blind critic named. The description
            lives in the click dialog. */}
        {notice === null ? null : (
          <div className="hs-town__plate hs-parch">
            <b>{notice.name}</b>
            <span>{notice.note}</span>
          </div>
        )}
      </div>

      {phone.phone ? (
        <button
          type="button"
          className="hs-town__fold"
          onClick={() => setConsoleOpen((o) => !o)}
        >
          {consoleOpen ? "Hide console" : "Town console"}
        </button>
      ) : null}

      {(!phone.phone || consoleOpen) ? (
      <div className="hs-town__rows"
        style={{ ...velvetVars(props.ownerColour), ...bustSkyVars(props.town.faction as string) } as CSSProperties}>
        <TownFootBar
          {...props}
          stats={stats}
          remaining={remaining}
          armies={armies}
          income={income}
          hovered={notice ?? hover}
          onBuild={() => setModal({ kind: "buildList" })}
          onRecruit={(c) => setModal({ kind: "recruit", creature: c })}
          onGuild={() => setModal({ kind: "guild" })}
          onTavern={() => setModal({ kind: "tavern" })}
          onCaravan={() => setModal({ kind: "caravan" })}
          onNotice={setNotice}
          onModal={setModal}
        />
        <TownResourceStrip resources={props.treasury} income={income} day={props.day} />
      </div>
      ) : null}

      {modal.kind === "build" ? (
        <BuildDialog
          content={content}
          town={town}
          treasury={props.treasury}
          building={modal.building}
          reason={props.buildReason(modal.building)}
          onBuild={(b) => { props.onCommand({ type: "BUILD", town: town.id, building: b }); setModal({ kind: "none" }); }}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "recruit" ? (
        <RecruitDialog
          content={content}
          town={town}
          treasury={props.treasury}
          army={props.visitingArmy ?? props.garrison}
          available={props.available}
          creature={modal.creature}
          reasonFor={props.recruitReason}
          onRecruit={(c, n) => { props.onCommand({ type: "RECRUIT", town: town.id, creature: c, count: n }); setModal({ kind: "none" }); }}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "buildList" ? (
        <BuildListDialog
          content={content}
          slots={slots}
          treasury={props.treasury}
          buildReason={props.buildReason}
          onPick={(b) => setModal({ kind: "build", building: b })}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "guild" ? (
        <GuildDialog content={content} town={town} onClose={() => setModal({ kind: "none" })} />
      ) : null}

      {modal.kind === "caravan" ? (
        <CaravanDialog
          content={content}
          town={town}
          treasury={props.treasury}
          offers={props.caravanOffers ?? []}
          inFlight={props.caravansOnTheRoad ?? []}
          onSend={(o, n) => {
            issue({
              type: "SEND_CARAVAN", town: town.id,
              fromTown: o.fromTown, fromObject: o.fromObject,
              creature: o.creature, count: n,
            });
          }}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "split" ? (
        <SplitDialog
          content={content}
          armies={armies}
          from={modal.from}
          to={modal.to}
          checkCommand={props.checkCommand}
          onSplit={issue}
          onClose={closeModal}
        />
      ) : null}

      {modal.kind === "stack" ? (
        <StackDialog
          content={content}
          armies={armies}
          at={modal.at}
          town={town.id}
          treasury={props.treasury}
          garrison={props.garrison}
          checkCommand={props.checkCommand}
          upgradeOffers={props.upgradeOffers}
          onSplit={(from, to) => { setModal({ kind: "split", from, to }); }}
          onDismiss={(at) => { setModal({ kind: "dismissStack", at }); }}
          onUpgrade={(cmd) => { issue(cmd); closeModal(); }}
          onClose={closeModal}
        />
      ) : null}

      {modal.kind === "dismissStack" ? (
        <DismissStackDialog
          content={content}
          armies={armies}
          at={modal.at}
          checkCommand={props.checkCommand}
          onDismiss={issue}
          onClose={closeModal}
        />
      ) : null}

      {modal.kind === "dismissHero" && props.visitingHero !== null ? (
        <DismissHeroDialog
          content={content}
          hero={props.visitingHero}
          army={props.visitingArmy}
          checkCommand={props.checkCommand}
          onDismiss={issue}
          onClose={closeModal}
        />
      ) : null}

      {modal.kind === "market" && props.tradeRate !== undefined && props.tradeReason !== undefined
        && town.owner !== null ? (
          <Marketplace
            content={content}
            player={town.owner}
            treasury={props.treasury}
            rate={props.tradeRate}
            tradeReason={props.tradeReason}
            onCommand={props.onCommand}
            onClose={() => setModal({ kind: "none" })}
          />
        ) : null}

      {modal.kind === "shop" ? (
        <ShopDialog
          content={content}
          town={town}
          treasury={props.treasury}
          hero={props.visitingHero}
          checkCommand={props.checkCommand}
          onBuy={(a) => {
            if (props.visitingHero === null) return;
            props.onCommand({
              type: "TOWN_BUY_ARTIFACT", hero: props.visitingHero.id, town: town.id, artifact: a,
            });
          }}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}

      {modal.kind === "tavern" ? (
        <TavernDialog
          content={content}
          offers={props.tavernOffers ?? []}
          treasury={props.treasury}
          reasonFor={props.hireReason}
          onHire={(h) => { props.onCommand({ type: "HIRE_HERO", town: town.id, hero: h }); setModal({ kind: "none" }); }}
          onClose={() => setModal({ kind: "none" })}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------- the foot bar

interface FootBarProps extends TownScreenProps {
  readonly stats: TownStats;
  /** Structures the town could still raise: what BUILD would actually offer. */
  readonly remaining: number;
  readonly armies: ReadonlyMap<string, ArmyState>;
  readonly onBuild: () => void;
  readonly onRecruit: (c: CreatureDefId) => void;
  readonly onGuild: () => void;
  readonly onTavern: () => void;
  readonly onCaravan: () => void;
  readonly income: Resources;
  /** What the pointer is over on the painting, for the status line. */
  readonly hovered: Hovered | null;
  readonly onNotice: (h: Hovered | null) => void;
  readonly onModal: (m: Modal) => void;
}

/** A drop target, with the kernel's verdict on it already in hand. */
interface DropTarget extends SlotRef {
  readonly reason: Reason | null;
}

/**
 * The bottom bar, edge to edge.
 *
 * Left: the garrison. Centre: the visiting hero, with a face and a name -
 * "VISITING: LEVEL 14" identifies nobody. Right: what the town has for sale
 * this week and the one control that spends gold. That right half used to be
 * nine hundred and sixty pixels of bare wood, half the width of the window.
 */
function TownFootBar(props: FootBarProps): JSX.Element {
  const [drag, setDrag] = useState<DragState | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const phone = usePhoneLayout();

  const { armies, content, onNotice, onModal, onCommand, checkCommand } = props;

  const setDragState = useCallback((next: DragState | null) => {
    dragRef.current = next;
    setDrag(next);
  }, []);

  /**
   * What a drop on this socket would ask the kernel for, and what the kernel
   * says about it. Asked on drag-enter, so the socket is already red and the
   * plate already says why before the player lets go - a drop that quietly
   * does nothing is the whole class of bug this round is about.
   *
   * Reads `dragRef`, not React state: a finger-drag can start and drop in the
   * same gesture, before the state from `onDragStart` has flushed.
   */
  const planFor = useCallback((armyId: string, slot: number): {
    readonly cmd: GameCommand | null; readonly reason: Reason | null;
  } => {
    const d = dragRef.current;
    if (d === null) return { cmd: null, reason: null };
    const from = armies.get(d.army);
    const to = armies.get(armyId);
    if (from === undefined || to === undefined) return { cmd: null, reason: null };
    const cmd = armyDropCommand({
      fromArmy: from, fromSlot: d.slot, toArmy: to, toSlot: slot, single: d.single,
    });
    if (cmd === null) return { cmd: null, reason: null };
    return { cmd, reason: checkCommand?.(cmd) ?? null };
  }, [armies, checkCommand]);

  const drop = useCallback((armyId: string, slot: number, mods: DropModifiers) => {
    const d = dragRef.current;
    const plan = planFor(armyId, slot);
    setDragState(null);
    setTarget(null);
    onNotice(null);
    if (d === null || plan.cmd === null) return;

    // A modifier means "ask me how many". It is read from the drop and from the
    // drag start, because Chromium starts no drag at all when one is already
    // held at mouse-down: pressing it during the drag is the gesture that
    // actually works, and both are accepted so neither habit is punished. The
    // dialog carries its own legality, so it opens even when moving the WHOLE
    // stack here would be refused - a hero's last stack can be split but not
    // handed over whole, and that difference is the dialog's to explain.
    if (d.single || mods.shiftKey || mods.altKey || mods.ctrlKey) {
      onModal({ kind: "split", from: { army: d.army, slot: d.slot }, to: { army: armyId, slot } });
      return;
    }

    if (plan.reason !== null) {
      // Refused. The command is NOT sent: the shell asked first, and a UI that
      // issues a command it knows is illegal is a legality bug. The player gets
      // the kernel's own sentence instead.
      onNotice({ name: "That move is refused", note: explainReason(content, plan.reason) });
      return;
    }
    onCommand(plan.cmd);
  }, [planFor, setDragState, onNotice, onModal, onCommand, content]);

  const hero = props.visitingHero;
  const heroDef = hero === null ? null : props.content.heroes.get(hero.def) ?? null;
  const lines = useMemo(
    () => dwellingLines(props.content, props.town),
    [props.content, props.town],
  );

  const onDragStart = useCallback(
    (a: string, sl: number, single: boolean) => {
      setDragState({ army: a, slot: sl, single });
      onNotice(single
        ? { name: "Splitting", note: "Drop on a socket to choose how many to move." }
        : phone.phone
          ? { name: "Moving the whole stack", note: "Drop on a socket to move it." }
          : { name: "Moving the whole stack", note: "Hold Shift before you let go to move only part of it." });
    },
    [onNotice, phone.phone, setDragState],
  );
  const onDragEnter = useCallback((a: string, sl: number) => {
    const plan = planFor(a, sl);
    setTarget({ army: a, slot: sl, reason: plan.reason });
    if (plan.cmd === null) { onNotice(null); return; }
    onNotice(plan.reason === null
      ? null
      : { name: "That move is refused", note: explainReason(content, plan.reason) });
  }, [planFor, onNotice, content]);

  const cancelDrag = useCallback(() => {
    setDragState(null);
    setTarget(null);
    onNotice(null);
  }, [onNotice, setDragState]);

  const onPick = useCallback((a: string, sl: number) => {
    if (townClickSwallowed()) return;
    if (phone.phone) {
      const army = armies.get(a);
      const filled = army !== undefined && (army.slots[sl] ?? null) !== null;
      const action = townSocketTap({
        armed: dragRef.current, army: a, slot: sl, filled,
      });
      if (action.kind === "select") {
        setDragState({ army: a, slot: sl, single: false });
        onNotice({
          name: "Stack selected",
          note: "Tap a socket to move it, or tap this one again to split or dismiss.",
        });
        return;
      }
      if (action.kind === "drop") {
        drop(action.army, action.slot, { shiftKey: false, altKey: false, ctrlKey: false });
        return;
      }
      if (action.kind === "ignore") return;
      setDragState(null);
      setTarget(null);
      onNotice(null);
    }
    const army = armies.get(a);
    if (army === undefined || (army.slots[sl] ?? null) === null) return;
    onModal({ kind: "stack", at: { army: a, slot: sl } });
  }, [phone.phone, armies, onModal, drop, onNotice, setDragState]);

  const targetTitle = target?.reason == null
    ? undefined
    : explainReason(content, target.reason);

  /* The status line, the reference's own: what the pointer is over, else the
     town and the day. Socket hovers are named here rather than on a plate
     under each socket, which is what let the sockets grow to busts. */
  const [socketHover, setSocketHover] = useState<string | null>(null);
  const [blockHover, setBlockHover] = useState<string | null>(null);
  const onHoverSlot = useCallback((armyId: string, slot: number | null) => {
    if (slot === null) { setSocketHover(null); return; }
    const army = armies.get(armyId);
    const st = army?.slots[slot] ?? null;
    const who = armyId === (props.garrison?.id as string | undefined) ? "garrison" : "visiting";
    if (st === null) { setSocketHover(`Empty socket (${who})`); return; }
    const def = content.creatures.get(st.def);
    setSocketHover(`Select ${def?.namePlural ?? "stack"} (${who})`);
  }, [armies, content, props.garrison]);
  const status = props.hovered !== null
    ? props.hovered.name
    : socketHover ?? blockHover ?? `${props.town.name}, a ${titleCase(props.town.faction as string)} town`;

  const caravans = (props.caravanOffers ?? []).length > 0 || (props.caravansOnTheRoad ?? []).length > 0;

  return (
    <div className="hs-tfoot">
      <TownConsoleHead
        content={props.content}
        town={props.town}
        crest={crestKey(props.town.faction as string)}
        income={props.income.gold}
        lines={lines}
        onRecruit={props.onRecruit}
        onHover={setBlockHover}
      />
      <div className="hs-tfoot__mid">
        {/* Garrison over the visiting army, the sockets of one standing
            exactly over the sockets of the other; the town's pennant heads the
            first row and the hero's face the second. */}
        <div className="hs-tfoot__row">
          <TownFlag name={props.town.name} onHover={setBlockHover} />
          <TownArmyBlock
            content={props.content}
            army={props.garrison}
            tone="garrison"
            drag={drag}
            target={target}
            targetTitle={targetTitle}
            onDragStart={onDragStart}
            onDragEnter={onDragEnter}
            onDrop={drop}
            onPointerCancel={cancelDrag}
            onPick={onPick}
            onHover={onHoverSlot}
          />
        </div>
        <div className="hs-tfoot__braid" aria-hidden="true" />
        <div className="hs-tfoot__row">
          <HeroFace hero={hero} def={heroDef}
            onOpen={hero === null ? undefined : () => props.onOpenHero?.(hero)}
            onHover={setBlockHover} />
          <TownArmyBlock
            content={props.content}
            army={props.visitingArmy}
            tone="hero"
            drag={drag}
            target={target}
            targetTitle={targetTitle}
            onDragStart={onDragStart}
            onDragEnter={onDragEnter}
            onDrop={drop}
            onPointerCancel={cancelDrag}
            onPick={onPick}
            onHover={onHoverSlot}
          />
        </div>
        <div className="hs-tfoot__status" role="status">
          <BitmapText face="serif" shadow scale={1}>{status}</BitmapText>
        </div>
      </div>
      <div className="hs-tfoot__side">
        {/* The reference's plates are pictures of the buildings they open, so
            each takes the town's OWN hall, guild and tavern off the painting's
            sheet. The frame is the same one the painting draws at full size. */}
        <TownIconButton glyph="keep" art={`town.${props.town.faction as string}.town_hall`}
          title={props.remaining === 0
            ? "This town is finished - there is nothing left to raise"
            : `Build (${groupDigits(props.remaining)} left to raise)`}
          disabled={props.remaining === 0} onClick={props.onBuild} />
        <TownIconButton glyph="guild" art={`town.${props.town.faction as string}.mage_guild_1`}
          title="Mage Guild - the spells this town can teach" onClick={props.onGuild} />
        <TownIconButton glyph="tavern" art={`town.${props.town.faction as string}.tavern`}
          title="Tavern - hire a hero" onClick={props.onTavern} />
        <TownIconButton glyph="scroll"
          title={props.town.autoBuild === true
            ? "Auto-build is on - this town builds one affordable structure each day"
            : "Auto-build is off - this town only builds when you tell it to"}
          pressed={props.town.autoBuild === true}
          onClick={() => props.onCommand({ type: "TOGGLE_AUTO_BUILD", town: props.town.id })} />
        {caravans ? (
          <TownIconButton glyph="caravan" title="Caravans - buy creatures elsewhere and have them walk here"
            onClick={props.onCaravan} />
        ) : null}
        {hero === null ? null : (
          <TownIconButton glyph="hero" title={`Dismiss ${heroDef?.name ?? "this hero"} - he and his army leave the game`}
            onClick={() => { onModal({ kind: "dismissHero" }); }} />
        )}
        <TownIconButton glyph="leave" tone="gold" wide label="Leave"
          title="Back to the adventure map" onClick={props.onClose} />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------- re-exports
// The painting moved to `town/scene.tsx` on 2026-09-17; these are the names
// it exported from here before, kept so no importer had to move.
export {
  AIRLIGHT, AIR_STOPS, DEPTH, NEAR_STOPS, PAINT_CSS, WARMTH, airBackAlpha, airFarAlpha,
  airSplitIndex, airStops, airlight, depth, haze, nearStops, warmStops, warmth,
} from "./town/scene.js";
export type { Warmth } from "./town/scene.js";
